<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog'
import { Plus, Edit, Trash2 } from 'lucide-vue-next'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'

interface ProjectWorkflow {
  id: number
  key: string
  name: string
  description: string | null
  summary_prompt: string | null
  sort_order: number
}

const router = useRouter()
const store = useStore()
const member = computed(() => store.getters.getMember)

const workflows = ref<ProjectWorkflow[]>([])
const loading = ref(false)

const dialogOpen = ref(false)
const editing = ref<ProjectWorkflow | null>(null)
const form = ref({ name: '', description: '', summary_prompt: '' })

const confirmDeleteDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingDelete = ref<ProjectWorkflow | null>(null)

const fetchWorkflows = async () => {
  loading.value = true
  try {
    const response = await API({ method: 'GET', url: `${HOST}/admin/workflows` })
    workflows.value = response.data
  } catch (error) {
    toast({ title: 'Error', description: 'Failed to fetch workflows', variant: 'destructive' })
  } finally {
    loading.value = false
  }
}

const openCreate = () => {
  editing.value = null
  form.value = { name: '', description: '', summary_prompt: '' }
  dialogOpen.value = true
}

const openEdit = (workflow: ProjectWorkflow) => {
  editing.value = workflow
  form.value = {
    name: workflow.name,
    description: workflow.description || '',
    summary_prompt: workflow.summary_prompt || ''
  }
  dialogOpen.value = true
}

const saveWorkflow = async () => {
  if (!form.value.name.trim()) {
    toast({ title: 'Name is required', variant: 'destructive' })
    return
  }
  try {
    const data = {
      name: form.value.name,
      description: form.value.description || null,
      summary_prompt: form.value.summary_prompt || null
    }
    if (editing.value) {
      await API({ method: 'PATCH', url: `${HOST}/admin/workflows/${editing.value.id}`, data })
      toast({ title: 'Workflow updated' })
    } else {
      await API({ method: 'POST', url: `${HOST}/admin/workflows`, data })
      toast({ title: 'Workflow created' })
    }
    dialogOpen.value = false
    await fetchWorkflows()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to save workflow'
    toast({ title: message, variant: 'destructive' })
  }
}

const promptDelete = (workflow: ProjectWorkflow) => {
  pendingDelete.value = workflow
  confirmDeleteDialog.value?.open()
}

const deleteWorkflow = async () => {
  if (!pendingDelete.value) return
  try {
    await API({ method: 'DELETE', url: `${HOST}/admin/workflows/${pendingDelete.value.id}` })
    toast({ title: 'Workflow deleted' })
    await fetchWorkflows()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to delete workflow'
    toast({ title: message, variant: 'destructive' })
  } finally {
    pendingDelete.value = null
  }
}

onMounted(() => {
  if (!member.value?.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchWorkflows()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />
    <div class="p-6 space-y-4 flex-1 overflow-auto">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold">Project Workflows</h1>
        <Button variant="default" size="sm" @click="openCreate">
          <Plus class="mr-2 h-4 w-4" />
          New Workflow
        </Button>
      </div>

      <div class="text-sm text-muted-foreground">
        Showing {{ workflows.length }} workflow{{ workflows.length !== 1 ? 's' : '' }}
      </div>

      <div v-if="loading" class="p-4 space-y-3">
        <div v-for="i in 5" :key="i" class="h-10 bg-muted rounded animate-pulse"></div>
      </div>

      <div v-else class="border rounded-md">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Key</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Summary prompt</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="workflow in workflows" :key="workflow.id" class="border-b hover:bg-muted/50">
              <td class="p-4 align-middle font-medium">{{ workflow.name }}</td>
              <td class="p-4 align-middle">
                <code class="text-xs text-muted-foreground">{{ workflow.key }}</code>
              </td>
              <td class="p-4 align-middle max-w-md">
                <div class="text-sm text-muted-foreground truncate">
                  {{ workflow.summary_prompt || '—' }}
                </div>
              </td>
              <td class="p-4 align-middle">
                <div class="flex items-center gap-1">
                  <Button variant="ghost" size="icon" class="h-8 w-8" @click="openEdit(workflow)" title="Edit workflow">
                    <Edit class="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" class="h-8 w-8" @click="promptDelete(workflow)" title="Delete workflow">
                    <Trash2 class="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </td>
            </tr>
            <tr v-if="workflows.length === 0">
              <td colspan="4" class="text-center py-8 text-muted-foreground text-sm">No workflows yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Dialog v-model:open="dialogOpen">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ editing ? 'Edit Workflow' : 'New Workflow' }}</DialogTitle>
        </DialogHeader>
        <div class="space-y-4">
          <div class="space-y-1">
            <Label>Name</Label>
            <Input v-model="form.name" placeholder="e.g. Wildlife & Forest Conservation" />
          </div>
          <div class="space-y-1">
            <Label>Description</Label>
            <Input v-model="form.description" placeholder="Short description" />
          </div>
          <div class="space-y-1">
            <Label>Summary prompt</Label>
            <Textarea v-model="form.summary_prompt" rows="4" placeholder="Project-level AI summary lens for this workflow" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="dialogOpen = false">Cancel</Button>
          <Button @click="saveWorkflow">{{ editing ? 'Save' : 'Create' }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <DialogConfirm
      ref="confirmDeleteDialog"
      title="Delete workflow?"
      description="This soft-deletes the workflow. Templates and projects linked to it keep working but lose the workflow link. This action cannot be undone."
      @confirm="deleteWorkflow"
    />
  </div>
</template>
